import { useState } from "react";

import { Button, Stack, TextField } from "@mui/material";

import { ErrorTypography } from "components/ErrorTypography";
import { useAPI, useShipmentPageContext } from "hooks";

interface Props {
  render: boolean;
  postURL: string;
  textFieldLabel: string;
  addPadding: boolean;
  // Fires once the question is persisted. The caller owns any analytics, so
  // this shared input stays free of them. Required rather than optional: a
  // consumer that silently forgets it is the exact gap this component was
  // uninstrumented by for so long.
  onQuestionSubmitted: VoidFunction;
}

export function SubmitQuestionMenu(props: Props) {
  const { render, postURL, textFieldLabel, addPadding, onQuestionSubmitted } =
    props;
  const { api } = useAPI();
  const [error, setError] = useState<null | string>(null);
  const [question, setQuestion] = useState<string>("");
  const { onRefresh } = useShipmentPageContext();

  // The callback is chained after the catch rather than inside the success
  // handler: a caller whose callback throws must not make a sent question read
  // as unsent, and must not stop the page refreshing.
  const postQuestion = () => {
    api<unknown>("post", postURL, { question })
      .then(() => {
        setError(null);
        if (onRefresh) {
          onRefresh();
        }
        return true;
      })
      .catch((e) => {
        setError(e?.response?.data?.message ?? "Failed to submit question");
        return false;
      })
      .then((submitted) => {
        if (submitted) {
          onQuestionSubmitted();
        }
      });
  };

  if (!render) {
    return null;
  }

  return (
    <Stack direction="column">
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          fullWidth
          label={textFieldLabel}
          onChange={(e) => setQuestion(e.target.value)}
          multiline
        />
        <Button
          disabled={question.length < 1}
          variant="contained"
          sx={addPadding ? { pl: "2rem", pr: "2rem" } : {}}
          onClick={postQuestion}
        >
          Send
        </Button>
      </Stack>
      <ErrorTypography error={error} />
    </Stack>
  );
}
